import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import useReactHookFormProps from 'hooks/useReactHookFormProps';
import useCreateHouseholdAndMembers from 'pages/AffordaHousehold/hooks.useCreateHouseholdAndMembers';
import useUpdateApplicationMutation from 'hooks/mutations/useUpdateApplicationMutation';
import useApplicationStatusQuery from 'hooks/queries/useApplicationStatusQuery';
import { FormattedMessage } from 'react-intl';
import { YES_NO_OPTIONS } from 'utils/constants';
import { useHouseholdMembersContext } from 'contexts/HouseholdMembersContext';
import useIntlToast from 'hooks/useIntlToast';
import { HOUSEHOLD_PATHS } from 'constants/paths';

const booleanOrNullToYesNo = (value) => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '';
};

export const determineIfStudentIsDisqualified = (studentStatus) => {
  if (!studentStatus) return false;

  const studentStatusValues = Array.isArray(studentStatus)
    ? studentStatus
    : Object.values(studentStatus ?? {});
  return studentStatusValues.length
    ? studentStatusValues.every((question) => question === false)
    : false;
};

export const useIsStudentDisqualified = () => {
  const { primaryHouseholdMember: primaryMember } =
    useHouseholdMembersContext();

  const hasData = Boolean(primaryMember && Object.keys(primaryMember).length);

  if (!hasData) return false;

  const studentStatuses = primaryMember
    ? [
        primaryMember.TANFAssistance,
        primaryMember.jobTrainingProgram,
        primaryMember.fosterProgram,
        primaryMember.marriedJointReturn,
        primaryMember.singleParentDependantChild,
      ]
    : null;

  return determineIfStudentIsDisqualified(studentStatuses);
};

const useFullTimeStudent = ({
  isActive,
  householdForm,
  setHouseholdForm,
  handleCompleteStep,
}) => {
  const { studentStatus } = householdForm;
  const showIntlToast = useIntlToast();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = useMemo(() => {
    return {
      marriedJointReturn: booleanOrNullToYesNo(
        studentStatus?.marriedJointReturn,
      ),
      singleParentDependantChild: booleanOrNullToYesNo(
        studentStatus?.singleParentDependantChild,
      ),
      jobTrainingProgram: booleanOrNullToYesNo(
        studentStatus?.jobTrainingProgram,
      ),
      fosterProgram: booleanOrNullToYesNo(studentStatus?.fosterProgram),
      TANFAssistance: booleanOrNullToYesNo(studentStatus?.TANFAssistance),
    };
  }, [studentStatus]);

  const ReactHookFormProps = useReactHookFormProps({
    defaultValues,
    mode: 'onChange',
  });

  const { control, isValid, getValues, reset } = ReactHookFormProps;

  const isStudentDisqualified = useIsStudentDisqualified();

  const { data: applicationStatuses } = useApplicationStatusQuery();
  const { createHouseholdAndMembers } = useCreateHouseholdAndMembers({
    householdForm,
  });
  const { mutateAsync: updateApplicationMutationMutateAsync } =
    useUpdateApplicationMutation();

  const fields = useMemo(() => {
    return [
      {
        variant: 'buttonGroup',
        label: (
          <FormattedMessage id="afforda.householdFullTimeStudent.question1" />
        ),
        name: 'marriedJointReturn',
        rules: {
          required: true,
        },
        options: YES_NO_OPTIONS,
        GridProps: {
          xs: 12,
        },
      },
      {
        variant: 'buttonGroup',
        label: (
          <FormattedMessage id="afforda.householdFullTimeStudent.question2" />
        ),
        name: 'singleParentDependantChild',
        rules: {
          required: ({ getValues }) => getValues('marriedJointReturn') === 'no',
        },
        watchFields: ['marriedJointReturn'],
        hidden: ({ getValues }) => getValues('marriedJointReturn') !== 'no',
        options: YES_NO_OPTIONS,
        GridProps: {
          xs: 12,
        },
      },
      {
        variant: 'buttonGroup',
        label: (
          <FormattedMessage id="afforda.householdFullTimeStudent.question3" />
        ),
        name: 'jobTrainingProgram',
        rules: {
          required: ({ getValues }) =>
            getValues('singleParentDependantChild') === 'no',
        },
        GridProps: {
          xs: 12,
        },
        options: YES_NO_OPTIONS,

        watchFields: ['singleParentDependantChild'],
        hidden: ({ getValues }) =>
          getValues('singleParentDependantChild') !== 'no',
      },
      {
        variant: 'buttonGroup',
        label: (
          <FormattedMessage id="afforda.householdFullTimeStudent.question4" />
        ),
        name: 'TANFAssistance',
        rules: {
          required: ({ getValues }) => getValues('jobTrainingProgram') === 'no',
        },
        options: YES_NO_OPTIONS,

        GridProps: {
          xs: 12,
        },
        watchFields: ['jobTrainingProgram'],
        hidden: ({ getValues }) => getValues('jobTrainingProgram') !== 'no',
      },
      {
        variant: 'buttonGroup',
        label: (
          <FormattedMessage id="afforda.householdFullTimeStudent.question5" />
        ),
        name: 'fosterProgram',
        rules: {
          required: ({ getValues }) => getValues('TANFAssistance') === 'no',
        },
        options: YES_NO_OPTIONS,
        GridProps: {
          xs: 12,
        },
        watchFields: ['TANFAssistance'],
        hidden: ({ getValues }) => getValues('TANFAssistance') !== 'no',
      },
    ].map((field) => ({
      ...field,
      id: 'fullTimeStudent',
      direction: { xs: 'column', sm: 'row' },
    }));
  }, []);

  const handleSubmit = useCallback(
    async (goToNextURL) => {
      const values = getValues();

      const studentStatus = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          value === 'yes' ? true : value === 'no' ? false : null,
        ]),
      );

      setHouseholdForm(
        (prevState) => ({
          ...prevState,
          studentStatus,
        }),
        { fields, control },
      );

      const finalIsStudentDisqualified =
        determineIfStudentIsDisqualified(studentStatus);

      if (finalIsStudentDisqualified) {
        try {
          setIsSubmitting(true);
          const deniedApplicationStatusId = applicationStatuses.find(
            ({ name }) => name === 'Denied',
          )?.id;

          await createHouseholdAndMembers({ ...householdForm, studentStatus });
          await updateApplicationMutationMutateAsync({
            applicationStatusId: deniedApplicationStatusId,
          });

          showIntlToast(
            'info',
            'app.toast.info.notEligibleBasedOnStudentStatus',
          );

          handleCompleteStep([
            HOUSEHOLD_PATHS.FULL_TIME_STUDENT,
            HOUSEHOLD_PATHS.SUMMARY,
          ]);

          goToNextURL(finalIsStudentDisqualified);
        } catch {
          showIntlToast('error', 'app.toast.error.unableToCheckStudentStatus');
        } finally {
          setIsSubmitting(false);
        }
      } else {
        goToNextURL(finalIsStudentDisqualified);
      }
    },
    [
      handleCompleteStep,
      getValues,
      setHouseholdForm,
      fields,
      control,
      applicationStatuses,
      createHouseholdAndMembers,
      householdForm,
      updateApplicationMutationMutateAsync,
      showIntlToast,
    ],
  );

  useLayoutEffect(() => {
    if (isActive) {
      reset(defaultValues);
    }
  }, [defaultValues, isActive, reset]);

  return {
    ReactHookFormProps,
    fields,
    handleSubmit,
    isSubmitting,
    isFormValid: isValid,
    isStudentDisqualified,
  };
};

export default useFullTimeStudent;
